<template>
    <div
        :class="
            'admin site-width' + (getMode === 'cancelcancer' ? '' : 'single')
        ">
        <Seo :title="$t('admin')" canonical="/admin" />

        <div class="menu-headline-distance" />
        <SiteHeadline
            is-main
            headline="Event"
            message="Cancel Cancer Berlinale 2026" />
        <div class="headline-message-distance" />

        <div class="admin-struct">
            <div v-if="!authorized">
                <div
                    class="uk-grid-small uk-grid-match uk-flex uk-flex-middle uk-flex-center"
                    uk-grid>
                    <div class="uk-width-medium">
                        <input
                            v-model="password"
                            :placeholder="$t('password')"
                            class="uk-input large-input uk-light"
                            name="email"
                            type="password"
                            v-on:keyup.enter="login()" />
                    </div>
                    <div class="uk-width-auto">
                        <span v-if="password.length > 0" v-on:click="login()">
                            <Button
                                :text="$t('login')"
                                is-large
                                is-min-width
                                mode="primary" />
                        </span>
                        <Button
                            v-else
                            :text="$t('login')"
                            :uk-tooltip="
                                'title:' + $t('pleaseEnterYourPassword')
                            "
                            is-large
                            is-min-width
                            mode="disabled" />
                    </div>
                </div>
                <div v-if="loginStatus === 401" class="uk-text-center">
                    <br />
                    {{ $t('invalidPassword') }}
                </div>
            </div>
            <div v-else>
                <div class="uk-margin-top" />

                <div class="uk-text-center" v-if="loading">
                    <span uk-spinner />
                </div>
                <div v-else>
                    <div class="uk-text-center uk-flex uk-flex-center">
                        <div class="uk-width-medium uk-margin-medium-bottom">
                            <input
                                v-model="search"
                                placeholder="Suche"
                                class="uk-input large-input"
                                style="color: #000 !important"
                                name="name" />
                        </div>
                    </div>

                    <div uk-filter="target: .js-filter">
                        <ul
                            class="js-filter uk-child-width-1-5@xl uk-child-width-1-4@l uk-child-width-1-3@m uk-child-width-1-2@s uk-child-width-1-1 uk-text-center uk-grid-match"
                            uk-grid>
                            <li
                                v-for="membership in computedInvites"
                                :key="membership.id"
                                :data-reply="
                                    membership.reply ??
                                    (membership.last_viewed_at
                                        ? 'viewed-unanswered'
                                        : 'notviewed-unanswered')
                                ">
                                <div
                                    class="uk-card uk-card-alternative uk-dark-card-hovered uk-card-body">
                                    <div class="uk-text-large">
                                        {{
                                            $t(membership.salutation) +
                                            ' ' +
                                            (membership.forename ?? '') +
                                            ' ' +
                                            (membership.surname ?? '')
                                        }}
                                    </div>
                                    <div
                                        class="uk-text-large"
                                        v-if="membership.partner">
                                        Mit Begleitung
                                    </div>
                                    <div class="uk-margin-small-top">
                                        {{ membership.email }}
                                    </div>
                                    <div class="uk-margin-small-top" />

                                    <hr />

                                    <div
                                        class="uk-text-success"
                                        v-if="membership.present_at">
                                        Erschienen am
                                        {{ convertDate(membership.present_at) }}
                                    </div>
                                    <div v-else>
                                        <span
                                            v-on:click="
                                                presentInvite(membership)
                                            ">
                                            <Button isLarge mode="success">
                                                Erschienen
                                            </Button>
                                        </span>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <div class="last-section-distance" />
    </div>
</template>

<script>
import Seo from '../components/Seo';
import SiteHeadline from '../components/SiteHeadline';
import axios from 'axios';
import Button from '@/components/Button';

export default {
    name: 'AdminBerlinale',
    components: {
        Button,
        SiteHeadline,
        Seo,
    },
    data() {
        return {
            password: '',
            authorized: false,
            loginStatus: null,

            invites: [],
            loading: true,

            search: '',
        };
    },
    computed: {
        computedInvites() {
            let items = this.invites;

            if (this.search.length > 0) {
                items = this.searcher(this.invites, this.search);
            }

            return items;
        },
    },
    created() {
        axios.defaults.timeout = this.apiTimeout;

        let password = this.$cookie.get('password');

        if (!password) {
            password = this.$route.query.token;
        }

        if (!password) {
            password = this.$route.query.password;
        }

        if (password) {
            this.password = password;
            this.login();
        } else {
            this.routeTo();
        }
    },
    methods: {
        searcher(array, value) {
            const lowerValue = value.toLowerCase();

            return array.filter((item) =>
                Object.values(item).some((prop) =>
                    String(prop).toLowerCase().includes(lowerValue)
                )
            );
        },
        convertDate(date) {
            let parsedDate = new Date(date);
            parsedDate.setHours(parsedDate.getHours() + 1);

            if (isNaN(parsedDate)) {
                return date;
            }

            let dateLang = 'en-US';
            if (this.$i18n.locale === 'de') {
                dateLang = 'de-DE';
            }

            return parsedDate
                .toLocaleDateString(dateLang, {
                    weekday: 'long',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                })
                .replace(/[^ -~]/g, '');
        },
        presentInvite(invite) {
            let http = axios.create();

            http.post(
                this.getBaseUrl +
                    '/api/invites/' +
                    invite.id +
                    '/actions/present'
            )
                .then(() => {
                    invite.present_at = new Date();
                })
                .catch(() => {});
        },
        getInvites() {
            let http = axios.create();

            http.get(this.getBaseUrl + '/api/invites/slim')
                .then((response) => {
                    this.invites = response.data;
                    this.loading = false;
                })
                .catch((e) => {
                    this.error = e;
                    this.loading = false;
                });
        },
        login() {
            axios.defaults.headers.common['password'] = this.password;

            let http = axios.create();
            http.post(this.getBaseUrl + '/api/login/inspector')
                .then(() => {
                    this.$cookie.set('password', this.password, 30);
                    this.authorized = true;
                    this.loginStatus = 200;

                    this.getInvites();
                })
                .catch((e) => {
                    this.loginStatus = 500;
                    if (e && e.response) {
                        this.loginStatus = e.response.status;
                    }
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.uk-button {
    text-transform: initial !important;
}

.admin-struct {
    min-height: calc(100vh - 70px - 70px - 177px - 279px);
}

.uk-table-striped > tr:nth-of-type(odd),
.uk-table-striped tbody tr:nth-of-type(odd) {
    background: $alternative-color !important;
    border-top: 1px solid #e5e5e5;
    border-bottom: 1px solid #e5e5e5;
}

.uk-table-hover > tr:hover,
.uk-table-hover tbody tr:hover {
    background: $alternative-color !important;
}

.uk-input {
    color: #ddd !important;
}

.uk-switcher {
    margin-top: 25px;
}

.uk-tab > * > a {
    text-transform: initial !important;
    font-size: 16px;
    font-weight: 400;
    border-color: transparent;
}

.customer-logo {
    max-width: 200px !important;
    max-height: 40px !important;
}

.video-item {
    height: 200px;
    margin-top: 5px;
}

.small {
    font-size: 15px;
}
</style>
